import { type RefObject, useEffect, useState } from 'react';

export interface IntersectionObserverHookOptions extends IntersectionObserverInit {
    /** If true, the observer disconnects once the target is visible. */
    triggerOnce?: boolean;
}

export interface IntersectionObserverHookResult {
    /** True if the target element is in view. */
    inView: boolean;
    /** The latest IntersectionObserverEntry. */
    entry: IntersectionObserverEntry | null;
}

/**
 * Observe target element visibility within viewport/root using IntersectionObserver API.
 * Supports passing either a ref or a direct Element. Cleans up automatically.
 */
export function useIntersectionObserver(
    target: RefObject<Element | null> | Element | null,
    options: IntersectionObserverHookOptions = { threshold: 0, triggerOnce: false },
): IntersectionObserverHookResult {
    const [inView, setInView] = useState(false);
    const [entry, setEntry] = useState<IntersectionObserverEntry | null>(null);

    useEffect(() => {
        const element = target && 'current' in target ? target.current : target;
        if (!element) return;

        const { triggerOnce, ...observerOptions } = options;
        let cancelled = false;

        const observer = new IntersectionObserver(entries => {
            if (cancelled) return;
            const firstEntry = entries[0];
            setEntry(firstEntry);
            setInView(firstEntry.isIntersecting);
            if (triggerOnce && firstEntry.isIntersecting) observer.disconnect();
        }, observerOptions);

        observer.observe(element);
        return () => {
            cancelled = true;
            observer.disconnect();
        };
    }, [target, options?.root, options?.rootMargin, options?.threshold, options?.triggerOnce]);

    return { inView, entry };
}

export default useIntersectionObserver;
